import { IAffiliate } from '@clickster/constant-definitions/dist-esm/entities/affiliate/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import orderBy from 'lodash/orderBy';
import { IQueryProps } from '../interfaces/general/api';
import { AffiliateService } from '../services/affiliates';

interface IUseGetAffiliatesNetwork extends IQueryProps<IAffiliate> {
  keyName?: string;
}

export const useGetAffiliatesNetwork = ({
  params,
  handleSuccess,
  handleError,
  keyName = '',
  enabled
}: IUseGetAffiliatesNetwork) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['affiliatesNetwork' + keyName], () => AffiliateService.list(params), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
    enabled
  });

  return {
    affiliates: orderBy(data ?? [], ['name'], ['asc']) as IAffiliate[],
    isLoading,
    isFetching,
    refetch,
  };
};

interface IUseGetAffiliateById extends IQueryProps<IAffiliate> {
  id: string;
}

export const useGetAffiliateById = ({ id = '', enabled, params, handleSuccess }: IUseGetAffiliateById) => {
  const {
    data = {},
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['affiliatesNetwork', id], () => AffiliateService.find(id, params), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
  });

  return {
    affiliate: (data ?? {}) as IAffiliate,
    isLoading,
    isFetching,
    refetch,
  };
};

export const useUpdateAffiliate = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (affiliate: IAffiliate) => AffiliateService.update(affiliate),
    {
      onError: (error: AxiosError) => console.log({ error }),
    },
  );

  return {
    updateAffiliate: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useCreateAffiliate = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (affiliate: IAffiliate) => AffiliateService.create(affiliate),
    {
      onError: (error: AxiosError) => console.log({ error }),
    },
  );

  return {
    createAffiliate: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};
